import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Lazy load a component with automatic retry on failure.
 * This helps handle chunk loading errors that occur after deployments
 * when old cached code references chunk files that no longer exist.
 * 
 * @param componentImport - The dynamic import function (e.g., () => import('./MyComponent'))
 * @param retries - Number of retry attempts (default: 2)
 * @param interval - Delay between retries in ms (default: 1500)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 2,
  interval = 1500
): LazyExoticComponent<T> {
  return lazy(() => retryImport(componentImport, retries, interval)) as LazyExoticComponent<T>;
}

/**
 * Initialize global error handlers for chunk loading failures.
 * Call this once at app startup to catch unhandled chunk errors.
 */
export function initChunkErrorHandler(): void {
  if (typeof window === 'undefined') return;
  
  // Prevent duplicate initialization
  if ((window as Window & { __chunkErrorHandlerInit?: boolean }).__chunkErrorHandlerInit) return;
  (window as Window & { __chunkErrorHandlerInit?: boolean }).__chunkErrorHandlerInit = true;
  
  window.addEventListener('error', (event) => {
    const error = event.error;
    if (error && isChunkLoadingError(error)) {
      console.error('🚨 Global chunk loading error detected:', error.message);
      // The error boundary should handle this, but log for debugging
    }
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error && isChunkLoadingError(error)) {
      console.error('🚨 Unhandled chunk loading promise rejection:', error.message);
      event.preventDefault(); // Prevent console noise
      
      // Show user-friendly notification if possible
      if (document.visibilityState === 'visible') {
        console.warn('💡 A new version may be available. Please refresh the page.');
      }
    }
  });
  
  console.log('✅ Chunk error handler initialized');
}

function isChunkLoadingError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Unexpected token '<'") ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically') ||
    error.message.includes('dynamically imported module') ||
    error.message.includes('error loading') ||
    error.message.includes('Loading CSS chunk')
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function retryImport<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries: number,
  interval: number
): Promise<{ default: T }> {
  try {
    return await componentImport();
  } catch (error) {
    // Check if this is a chunk loading error
    const isChunkError = 
      error instanceof Error && (
        error.message.includes("Unexpected token '<'") ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Failed to fetch dynamically') ||
        error.message.includes('dynamically imported module') ||
        error.message.includes('error loading')
      );

    if (isChunkError && retries > 0) {
      console.warn(`⚠️ Chunk loading failed, retrying... (${retries} attempts left)`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, interval));
      
      // Clear caches before retry (helps with stale chunks)
      if (retries === 1) {
        // On last retry, try clearing caches
        await clearBrowserCaches();
      }
      
      return retryImport(componentImport, retries - 1, interval);
    }
    
    // If not a chunk error or no retries left, rethrow
    throw error;
  }
}

async function clearBrowserCaches(): Promise<void> {
  try {
    // Clear Cache API caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('🗑️ Browser caches cleared before retry');
    }
    
    // Tell service worker to clear its cache
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (registration.active) {
          registration.active.postMessage({ type: 'CLEAR_CACHE' });
        }
      }
    }
  } catch (e) {
    console.warn('Failed to clear caches:', e);
  }
}

export default lazyWithRetry;
